<template>
  <div id="entries"
    class="w-full h-full min-h-[300px] p-3 border border-stone-300 dark:border-stone-600 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-stone-700 dark:text-stone-200 overflow-auto font-sans"
    spellcheck="false" contentEditable="true" v-on:paste="onPaste" v-on:input="setEntries()" v-on:keyup="IE_setEntries">
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useWheelStore } from '@/stores/useWheelStore';
import * as Util from '@utils/Util.js';

const wheelStore = useWheelStore();
const updateTriggeredByMe = ref(false);

const wheelConfig = computed(() => wheelStore.wheelConfig);
const entries = computed(() => wheelConfig.value?.entries || []);
const wheelIsBusy = computed(() => wheelStore.wheelIsBusy);
const sheetLinked = computed(() => wheelStore.sheetLinked);

function setEntries() {
  updateTriggeredByMe.value = true;
  const newEntries = getEntries();
  wheelStore.setEntries(newEntries);
}

function IE_setEntries() {
  if (Util.browserIsIE()) {
    setEntries();
  }
}

function getEntries() {
  const div = document.getElementById('entries');
  return Util.getEntriesFromHtml(div.innerHTML);
}

function setTextboxContents(newEntries) {
  const div = document.getElementById('entries');
  if (div) div.innerHTML = newEntries.map(entry => Util.renderEntry(entry)).join('');
}

function onPaste(e) {
  // Intercept paste into the text-box. Transform rich text into plain text, unless a data image is being pasted.
  e.preventDefault();
  Util.trackEvent('Wheel', 'PasteIntoTextbox', '');
  if (e.clipboardData) {
    // Modern browsers.
    let html = (e.originalEvent || e).clipboardData.getData('text/html');
    let match = html.match(/(<.*?src="data:image.*?>)/);
    if (match) {
      document.execCommand('insertHtml', false, match[1]);
    } else {
      let text = (e.originalEvent || e).clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    }
  } else {
    // Internet Explorer.
    const clipboardData = window.clipboardData.getData('text');
    if (clipboardData) {
      wheelStore.appendTextEntries(clipboardData.split(/\n/));
    }
  }
}

onMounted(() => {
  setTextboxContents(entries.value);
});

watch(entries, (newEntries) => {
  if (updateTriggeredByMe.value) {
    updateTriggeredByMe.value = false;
  } else {
    setTextboxContents(newEntries);
  }
});

watch(wheelConfig, (newConfig) => {
  setTextboxContents(newConfig.entries || []);
});

watch(wheelIsBusy, (isBusy) => {
  const editable = !isBusy;
  const textBoxDiv = document.getElementById('entries');
  if (textBoxDiv) textBoxDiv.setAttribute('contenteditable', editable);
});

watch(sheetLinked, (isLinked) => {
  const editable = !isLinked;
  const textBoxDiv = document.getElementById('entries');
  if (!textBoxDiv) return;
  textBoxDiv.setAttribute('contenteditable', editable);
  if (isLinked) {
    textBoxDiv.style.backgroundImage = "url('/images/link.png')";
    textBoxDiv.style.backgroundSize = "100%";
    textBoxDiv.style.backgroundAttachment = "local";
  } else {
    textBoxDiv.style.backgroundImage = "";
  }
});

// Functions are in scope for template events: onPaste, setEntries, IE_setEntries
</script>

<style scoped>
/* Additional styles that can't be handled with Tailwind */
</style>
